'use client';

import { useEffect, useRef } from 'react';
import { IPlatform, IPlugin } from '../../types';
import { Faq } from '../faq/faq.comp';
import { WidgetNavbar } from '../widgetNavbar/widgetNavbar.comp';
import { StyledEmbedPricing, StyledPricing } from './pricing.styles';

// eslint-disable-next-line @typescript-eslint/no-require-imports
require('@commonninja/commonninja-auth-sdk');

const { NEXT_PUBLIC_PRICING_EMBED_URL } = process.env;

export const Pricing = ({
	widget,
	platform,
	summary,
}: {
	widget?: IPlugin;
	platform?: IPlatform;
	/**
	 * Server-rendered plan/price summary, passed in from the page so the prices
	 * land in the initial HTML (DEV-14679). The embed below is an iframe, so
	 * nothing inside it is visible to crawlers of this page.
	 */
	summary?: React.ReactNode;
}) => {
	const pricingRef = useRef() as any;

	useEffect(() => {
		if (typeof window !== 'undefined') {
			let embedUrl = `website/pricing/${widget?.serviceName || 'bundles'}`;

			if (window.location.search) {
				embedUrl += window.location.search;
			}	

			(window as any).CommonninjaAuthSDK.baseUrl = NEXT_PUBLIC_PRICING_EMBED_URL;
			(window as any).CommonninjaAuthSDK.embedDashboardView(
				pricingRef?.current,
				embedUrl
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{widget && (
				<StyledPricing>
					<WidgetNavbar widget={widget} platform={platform} />
				</StyledPricing>
			)}
			{/* Pricing component in "commonninja-users" repo:
			https://github.com/CommonNinja/commonninja-users
			*/}
			<StyledEmbedPricing
				className={`embedded-pricing`}
				ref={pricingRef}
			></StyledEmbedPricing>
			{summary}
			<Faq faqs={faqs} />
		</>
	);
};

const faqs = [
	{
		q: 'Can I cancel my subscription?',
		a: 'Yes, you can cancel your subscription at any time.',
	},

	{
		q: 'How do I cancel my subscription?',
		a: 'To cancel your subscription, go to your Common Ninja dashboard, and look for the “Billing” menu. From there, find the subscription you want to cancel and click “Cancel”.',
	},

	{
		q: ' What happens when I cancel my subscription?',
		a: 'After you cancel your subscription, you will still be able to continue using the widgets till the end of the month that you paid for.',
	},
	{
		q: 'What happens when my subscription expires',
		a: 'You can still use your widgets, but they will revert to the free plan cancelling all premium features.',
	},
	{
		q: 'Can I upgrade my plan?',
		a: 'Of course! We encourage you to! Choose the plan you want, and in the pricing page click the “Switch Plan” button under the plan you want to change to.',
	},

	{
		q: 'Can I downgrade my plan? ',
		a: 'Yes, you can. Choose the plan you want to downgrade, and in the pricing page, click the “Switch Plan” button under the plan you want to change to.',
	},
	{
		q: 'What happens if I downgrade my plan? ',
		a: 'Depending on your plan, the number of widgets and some features will become more limited or unavailable.',
	},

	{
		q: 'Can I have a customized plan?',
		a: ' Certainly! We aim to cater to all our users’ needs! Contact us and let us create a tailored plan just for you. ',
	},
	{
		q: 'Is my subscription renewed automatically? ',
		a: 'Yes. You can, however, cancel it whenever you like.',
	},

	{
		q: 'Does the plan price include VAT?',
		a: 'No, VAT might be calculated separately, based on your country. ',
	},

	{
		q: 'I deleted Common Ninja’s widget, why am I still being charged?',
		a: 'Deleting the widget does not cancel the subscription. To cancel the subscription, you need to go to your Common Ninja dashboard, and look for the “Billing” menu. From here, find the subscription to and click “Cancel”.',
	},

	{
		q: 'Will I be refunded if I cancel before the end of the month?',
		a: 'You can check the refund policy in our help center. You will, however, be able to continue using the widgets till the end of the month you paid for.',
	},

	{
		q: 'How do I update my payment details?',
		a: 'To update your payment details, contact our support team at support@commoninja.com and we will be happy to help you.',
	},
	{
		q: 'I’m switching to a new platform, do I need to cancel my subscription?',
		a: 'No. You can keep using Common Ninja’s widgets on any new platform. For platform specific guides, please refer to our Knowledge Base articles.',
	},
	{
		q: ' Can I try a Common Ninja app before purchasing?',
		a: ' Certainly! We have a free plan available for all our widgets.',
	},
	{
		q: 'What payment methods does Common Ninja accept? ',
		a: 'We accept all major credit cards and payments through PayPal.',
	},
];
